import {
  commonCountryOptionsFlat,
  GLOBAL_COUNTRY,
  isGlobalCountry,
} from '../../utils/formOptions';
import type { Geography, Profile } from './types';

export type { Geography } from './types';
// `Extracted` remains the name used across the sidebar/components; it is now the
// canonical local Profile type (swap to the generated schema after Orval regen).
export type Extracted = Profile;

export const EMPTY_GEOGRAPHY: Geography = {
  country: null,
  region: null,
  city: null,
};

/** Max length for the brand description (its own, wider cap). */
export const DESCRIPTION_MAX_LENGTH = 1000;

/** Max length for every other editable profile field. Mirrors the backend cap. */
export const FIELD_MAX_LENGTH = 255;

/** Cap for country/region/city; clears the longest real place names (~55-60). */
export const GEOGRAPHY_MAX_LENGTH = 100;

/**
 * ISO alpha-2 code → country name, for displaying the stored country code as a
 * readable name. Built from the shared country picker list.
 */
const COUNTRY_CODE_TO_LABEL: Record<string, string> = Object.fromEntries(
  commonCountryOptionsFlat
    .filter((option) => option.value !== 'OTHER')
    .map((option) => [option.value, option.label]),
);

/** Render a stored ISO alpha-2 country code as its country name (falls back to the code). */
export const countryLabel = (code: string | null | undefined): string => {
  const trimmed = (code ?? '').trim();
  if (!trimmed) return '';
  // The worldwide sentinel isn't an ISO code — show it as "Global".
  if (isGlobalCountry(trimmed)) return GLOBAL_COUNTRY;
  return COUNTRY_CODE_TO_LABEL[trimmed.toUpperCase()] ?? trimmed;
};

export const ORDERED_FIELDS: Array<keyof Extracted> = [
  'brand_name',
  'description',
  'website_url',
  'target_language',
  'target_geography',
  'key_offerings',
  'brand_aliases',
  'target_audience',
  'industry',
  'competitors',
  'unique_value',
];

export const FIELD_LABELS: Record<keyof Extracted, string> = {
  brand_name: 'Brand name',
  description: 'Description',
  website_url: 'Website',
  target_language: 'Language',
  target_geography: 'Country',
  key_offerings: 'Key offerings',
  brand_aliases: 'Brand aliases',
  target_audience: 'Customer type',
  industry: 'Industry',
  competitors: 'Competitors',
  unique_value: 'Unique value',
};

/**
 * Short explanatory tooltips shown next to each field label to clarify what the
 * field expects (some names — "Country", "Customer type", "Brand aliases" — are
 * easy to misread). Typed as a full record so adding a field to `Extracted`
 * without tooltip copy is a type error.
 */
export const FIELD_TOOLTIPS: Record<keyof Extracted, string> = {
  brand_name: "The name of your brand as it's officially known.",
  description:
    "A short summary of what your brand does, who it's for, and what makes it different.",
  website_url:
    "Your brand's main website. We use this to help generate more relevant suggestions.",
  target_language: 'The main language your target audience speaks.',
  target_geography:
    'The country where your target audience is, not necessarily where your brand is based. If you operate globally, this will show as "Global."',
  key_offerings: 'The main products or services your brand is known for.',
  brand_aliases:
    'Other names your brand is known by, such as nicknames, abbreviations, or former names.',
  target_audience:
    'Who typically buys from your brand: final consumers, other businesses, or both.',
  industry:
    'The sector your brand operates in, such as fashion, finance, or software.',
  competitors: 'Other brands your target audience might compare you to.',
  unique_value:
    'What sets your brand apart from competitors — the main reason customers choose you.',
};

/**
 * Tooltips for the Region and City cards. These are derived from the single
 * `target_geography` object rather than being their own `Extracted` keys, so
 * they can't live in `FIELD_TOOLTIPS`.
 */
export const REGION_TOOLTIP =
  'State, province, or equivalent to a specific geographic territory. Only shown if your brand serves a specific region instead of an entire country.';

export const CITY_TOOLTIP =
  'Only shown if your brand serves a specific city instead of an entire region or country.';

const ARRAY_FIELDS = new Set<keyof Extracted>([
  'key_offerings',
  'brand_aliases',
  'competitors',
]);

export const isArrayField = (field: keyof Extracted): boolean =>
  ARRAY_FIELDS.has(field);

/**
 * Fields edited in a multi-line textarea rather than a single-line input. These
 * hold long text (description, unique value) or comma-separated lists that don't
 * fit on one line — a single-line input would show only the first few words.
 */
const MULTILINE_FIELDS = new Set<keyof Extracted>([
  'description',
  'key_offerings',
  'brand_aliases',
  'target_audience',
  'competitors',
  'unique_value',
]);

export const isMultilineField = (field: keyof Extracted): boolean =>
  MULTILINE_FIELDS.has(field);

/**
 * Fields the user must fill before a brand can be generated. Single source of
 * truth for both the per-field "required" markers in the sidebar and the
 * `meetsEnough` gate on the generate button.
 */
export const REQUIRED_FIELDS = new Set<keyof Extracted>([
  'brand_name',
  'description',
]);

export const isRequiredField = (field: keyof Extracted): boolean =>
  REQUIRED_FIELDS.has(field);

/**
 * Prefix a URL with `https://` unless it already starts with `http://` or
 * `https://`, so scheme-less input like `acme.com` is accepted. (Any other
 * scheme, e.g. `ftp://`, is not recognised and gets the `https://` prefix — but
 * `validateWebsiteUrl` rejects such values before they reach the payload.)
 * Shared by the Website-field validator and `brandPayload.ts`'s normalizer.
 */
export const ensureUrlScheme = (url: string): string =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

/**
 * Validate the Website field. Returns an error message when invalid, or `null`
 * when valid. Empty is valid — the field is optional. Bare domains are accepted
 * (normalized to `https://` before checking), matching `normalizeUrl`; a dotless
 * host (`asdf`, `localhost`) or unparseable input (`not a url`) is rejected.
 */
export const validateWebsiteUrl = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(ensureUrlScheme(trimmed));
    const isHttp = url.protocol === 'http:' || url.protocol === 'https:';
    if (isHttp && url.hostname.includes('.')) return null;
  } catch {
    // Unparseable — falls through to the error below.
  }
  return 'Please enter a valid website URL.';
};

export const isFieldFilled = (field: keyof Extracted, value: unknown): boolean => {
  if (field === 'target_geography') {
    const geo = (value ?? EMPTY_GEOGRAPHY) as Geography;
    return Boolean(geo.country && geo.country.trim());
  }
  if (isArrayField(field)) {
    return Array.isArray(value) && value.length > 0;
  }
  return typeof value === 'string' && value.trim().length > 0;
};

export const formatFieldDisplay = (
  field: keyof Extracted,
  value: unknown,
): string => {
  if (!isFieldFilled(field, value)) return '';
  if (field === 'target_geography') {
    const geo = value as Geography;
    return [countryLabel(geo.country), geo.region, geo.city]
      .filter(Boolean)
      .join(', ');
  }
  if (isArrayField(field)) {
    return (value as string[]).join(', ');
  }
  return String(value);
};

export const formatGeographyEdit = (geo: Geography): string => {
  const parts: string[] = [];
  if (geo.country) parts.push(`country=${geo.country}`);
  if (geo.region) parts.push(`region=${geo.region}`);
  if (geo.city) parts.push(`city=${geo.city}`);
  return parts.join(', ');
};

export const formatFieldForEdit = (
  field: keyof Extracted,
  value: unknown,
): string => {
  if (field === 'target_geography') {
    return formatGeographyEdit((value ?? EMPTY_GEOGRAPHY) as Geography);
  }
  if (isArrayField(field)) {
    return Array.isArray(value) ? (value as string[]).join(', ') : '';
  }
  return value == null ? '' : String(value);
};

export const parseFieldFromEdit = (
  field: keyof Extracted,
  raw: string,
): Extracted[keyof Extracted] => {
  const trimmed = raw.trim();
  if (field === 'target_geography') {
    if (!trimmed) return EMPTY_GEOGRAPHY as Extracted[keyof Extracted];
    const next: Geography = { country: null, region: null, city: null };
    trimmed.split(',').forEach((part) => {
      const [key, ...rest] = part.split('=');
      const value = rest.join('=').trim();
      const slot = key?.trim().toLowerCase();
      if (slot === 'country') next.country = value || null;
      else if (slot === 'region') next.region = value || null;
      else if (slot === 'city') next.city = value || null;
      else if (!next.country) next.country = part.trim() || null;
    });
    return next as Extracted[keyof Extracted];
  }
  if (isArrayField(field)) {
    return trimmed
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean) as Extracted[keyof Extracted];
  }
  return (trimmed || null) as Extracted[keyof Extracted];
};

export const meetsEnough = (extracted: Extracted | null): boolean =>
  Boolean(extracted)
  && [...REQUIRED_FIELDS].every((field) => isFieldFilled(field, extracted?.[field]));

export const meetsOptimal = (extracted: Extracted | null): boolean =>
  countFilled(extracted) >= 6;

export const countFilled = (extracted: Extracted | null): number => {
  if (!extracted) return 0;
  return ORDERED_FIELDS.reduce(
    (n, field) => (isFieldFilled(field, extracted[field]) ? n + 1 : n),
    0,
  );
};

export const TOTAL_FIELDS = ORDERED_FIELDS.length;

export const isComplete = (extracted: Extracted | null): boolean =>
  countFilled(extracted) === TOTAL_FIELDS;
